import { Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { LineChart } from '@mui/x-charts/LineChart';
import { format, parseISO } from 'date-fns';
import { DailyTicketCount } from '../../modules/location/model';

interface TicketsLineChartProps {
  data: DailyTicketCount[];
  days: number;
  onDaysChange: (days: number) => void;
}

const BRAND_BLUE = '#06A8E1';
const DAY_RANGES = [14, 30, 60, 90];
// ile etykiet dat maksymalnie pokazujemy na osi X, żeby się nie zlewały
const MAX_X_LABELS = 14;

const TicketsLineChart = ({ data, days, onDaysChange }: TicketsLineChartProps) => {
  const xLabels = data.map((d) => format(parseISO(d.day), 'dd.MM'));
  const values = data.map((d) => d.count);
  const total = values.reduce((sum, v) => sum + v, 0);

  // co ile punktów pokazać etykietę; liczone od końca, żeby ostatni dzień był zawsze podpisany
  const labelStep = Math.ceil(xLabels.length / MAX_X_LABELS);
  const lastIndex = xLabels.length - 1;

  return (
    <Paper
      sx={{
        p: 2,
        border: '1px solid #E2E8F0',
        boxShadow: '10px 5px 10px #EFEFEF',
        borderRadius: '10px',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 0.5 }} spacing={1}>
        <Typography sx={{ fontWeight: '800', fontSize: '1.71rem' }}>Wydrukowane bilety</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_, value) => {
            if (value !== null) {
              onDaysChange(value as number);
            }
          }}
          aria-label="Zakres dni"
        >
          {DAY_RANGES.map((range) => (
            <ToggleButton key={range} value={range} aria-label={`Ostatnie ${range} dni`} sx={{ px: 1.5, fontWeight: 700 }}>
              {range} dni
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <Typography sx={{ fontWeight: '600', fontSize: '1rem', color: '#64748B', mb: 1 }}>
        Ostatnie {xLabels.length} dni — łącznie {total} szt.
      </Typography>
      <LineChart
        height={340}
        xAxis={[
          {
            data: xLabels,
            scaleType: 'point',
            label: 'Dzień',
            tickLabelInterval: (_value, index) => (lastIndex - index) % labelStep === 0,
          },
        ]}
        yAxis={[{ label: 'Liczba wydruków' }]}
        series={[
          {
            data: values,
            label: 'Wydrukowane bilety',
            color: BRAND_BLUE,
            curve: 'linear',
            showMark: xLabels.length <= 31,
          },
        ]}
        margin={{ left: 80, right: 24, top: 24, bottom: 56 }}
        sx={{
          // etykieta osi Y jest pozycjonowana sztywno (~28px od osi) i nachodzi
          // na trzycyfrowe wartości ticków — odsuwamy ją w lewo
          [`& .${axisClasses.left} .${axisClasses.label}`]: {
            transform: 'translateX(-14px)',
          },
        }}
      />
    </Paper>
  );
};

export default TicketsLineChart;
